import React, { useState, useCallback } from 'react'
import {
  Form, Button, InputNumber, Input, DatePicker, Upload, List, Card, Modal,
  Descriptions, Space, Empty, Spin, Popconfirm, Divider, Typography, Row, Col, Alert,
} from 'antd'
import {
  PlusOutlined, DeleteOutlined, EyeOutlined, CameraOutlined, VideoCameraOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import useReports from '../../../hooks/useReports'
import { BACKEND_URL } from '../../../services/backendURL'
import { BRAND, BRAND_DIVIDER } from '../constants'

const { TextArea } = Input
const { Text } = Typography

// Format backend errors into readable messages
function formatErrors(errors) {
  if (!errors) return []
  if (errors._general) return [errors._general]
  const messages = []
  Object.entries(errors).forEach(([field, msgs]) => {
    const fieldName = FIELD_LABELS[field] || field
    if (Array.isArray(msgs)) {
      msgs.forEach((m) => messages.push(`${fieldName}: ${m}`))
    } else if (typeof msgs === 'string') {
      messages.push(`${fieldName}: ${msgs}`)
    }
  })
  return messages
}

const FIELD_LABELS = {
  os_id: 'Njesia organizative (OS)',
  membertask_id: 'Detyra',
  meeting_date: 'Data e mbledhjes',
  participants_count: 'Numri i pjesemarresve',
  video: 'Video',
  images_list: 'Foto',
}

export default function Reports({ membertaskId, disabled }) {
  const { reports, loading, submitting, createReport, deleteReport, getReportDetails } = useReports(membertaskId)
  const [form] = Form.useForm()
  const [showForm, setShowForm] = useState(false)
  const [imageList, setImageList] = useState([])
  const [videoFile, setVideoFile] = useState(null)
  const [formErrors, setFormErrors] = useState([])
  const [detailModal, setDetailModal] = useState({ visible: false, data: null, loading: false })

  const handleSubmit = useCallback(async (values) => {
    setFormErrors([])
    const data = {
      ...values,
      meeting_date: values.meeting_date.format('YYYY-MM-DDTHH:mm:ss'),
      images: imageList.map((f) => f.originFileObj || f),
      video: videoFile?.originFileObj || videoFile,
    }
    const result = await createReport(data)
    if (result.success) {
      form.resetFields()
      setImageList([])
      setVideoFile(null)
      setShowForm(false)
      setFormErrors([])
    } else {
      // Show backend errors in the form
      const errorMessages = formatErrors(result.errors)
      if (errorMessages.length === 0) {
        errorMessages.push('Pati nje gabim gjate ruajtjes se raportit.')
      }
      setFormErrors(errorMessages)
    }
  }, [createReport, form, imageList, videoFile])

  const handleViewDetails = useCallback(async (id) => {
    setDetailModal({ visible: true, data: null, loading: true })
    try {
      const data = await getReportDetails(id)
      setDetailModal({ visible: true, data, loading: false })
    } catch {
      setDetailModal({ visible: false, data: null, loading: false })
    }
  }, [getReportDetails])

  const getMediaUrl = (url) => {
    if (!url) return ''
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`
  }

  return (
    <div>
      {!disabled && (
        <div style={{ marginBottom: 16 }}>
          {!showForm ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setShowForm(true); setFormErrors([]) }}
              style={{ backgroundColor: BRAND, borderColor: BRAND }}
              aria-label="Krijo raport"
            >
              Krijo raport
            </Button>
          ) : (
            <Card
              size="small"
              style={{ border: `1px solid ${BRAND_DIVIDER}` }}
              title={<span style={{ color: BRAND }}>Raport i ri mbledhje</span>}
            >
              {formErrors.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  onClose={() => setFormErrors([])}
                  style={{ marginBottom: 16 }}
                  message="Gabim ne ruajtjen e raportit"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {formErrors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  }
                />
              )}

              <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 8 }}
                labelAlign="left"
                onFinish={handleSubmit}
              >
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="os_id"
                      label="OS (Njesia)"
                      rules={[{ required: true, message: 'Vendosni ID e njesise organizative' }]}
                      extra="ID e njesise organizative (OS) ku u mbajt mbledhja"
                    >
                      <Input style={{ width: '100%' }} placeholder="p.sh. 0654" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="meeting_date"
                      label="Data"
                      rules={[{ required: true, message: 'Zgjidhni daten e mbledhjes' }]}
                    >
                      <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }}>
                  <span style={{ color: BRAND, fontSize: 13 }}>Pjesemarresit</span>
                </Divider>

                <Row gutter={24}>
                  <Col xs={12} sm={6}>
                    <Form.Item name="participants_count" label="Gjithsej">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="males_count" label="Meshkuj">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="females_count" label="Femra">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="youngs_count" label="Te rinj">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={12} sm={8}>
                    <Form.Item name="free_professions_count" label="Prof. te lira">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Form.Item name="new_members_count" label="Anetare te rinj">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="new_members_names" label="Emrat e anetareve te rinj">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="headers_names" label="Kryetaret e zgjedhur">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="proposed_delegate_name" label="I Deleguari i OS">
                  <Input />
                </Form.Item>
                <Form.Item name="comment" label="Koment">
                  <TextArea rows={3} />
                </Form.Item>

                <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }}>
                  <span style={{ color: BRAND, fontSize: 13 }}>Media</span>
                </Divider>

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    listType="picture-card"
                    fileList={imageList}
                    onChange={({ fileList }) => setImageList(fileList.slice(-10))}
                    beforeUpload={() => false}
                    accept="image/*"
                  >
                    {imageList.length < 10 && (
                      <div>
                        <CameraOutlined />
                        <div style={{ marginTop: 4 }}>Foto</div>
                      </div>
                    )}
                  </Upload>

                  <Upload
                    maxCount={1}
                    fileList={videoFile ? [videoFile] : []}
                    onChange={({ fileList }) => setVideoFile(fileList[0] || null)}
                    beforeUpload={() => false}
                    accept="video/mp4,video/3gpp,video/x-m4v"
                  >
                    <Button icon={<VideoCameraOutlined />}>Video</Button>
                  </Upload>
                </Space>

                <Divider />
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    style={{ width: 160, height: 37, backgroundColor: BRAND, borderColor: BRAND }}
                  >
                    Ruaj raportin
                  </Button>
                  <Button onClick={() => { setShowForm(false); form.resetFields(); setImageList([]); setVideoFile(null); setFormErrors([]) }}>
                    Anullo
                  </Button>
                </Space>
              </Form>
            </Card>
          )}
        </div>
      )}

      <Spin spinning={loading}>
        {reports.length === 0 && !loading ? (
          <Empty description="Nuk ka raporte" />
        ) : (
          <List
            dataSource={reports}
            renderItem={(report) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(report.id)}
                    style={{ color: BRAND }}
                    aria-label="Shiko detajet"
                  >
                    Shiko
                  </Button>,
                  ...(!disabled
                    ? [
                      <Popconfirm
                        key="delete"
                        title="Fshi raportin?"
                        onConfirm={() => deleteReport(report.id)}
                        okText="Po"
                        cancelText="Jo"
                      >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} aria-label="Fshi raportin" />
                      </Popconfirm>,
                    ]
                    : []),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Text>
                      Mbledhje - {report.meeting_date ? moment(report.meeting_date).format('DD/MM/YYYY HH:mm') : ''}
                    </Text>
                  }
                  description={
                    <Space>
                      <Text type="secondary">Pjesemarres: {report.participants_count || 0}</Text>
                      {report.os?.name && <Text type="secondary">| {report.os.name}</Text>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>

      <Modal
        title={<span style={{ color: BRAND }}>Detajet e raportit</span>}
        visible={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, data: null, loading: false })}
        footer={
          <Button onClick={() => setDetailModal({ visible: false, data: null, loading: false })}>
            Mbyll
          </Button>
        }
        width={700}
        centered
        destroyOnClose
      >
        {detailModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : detailModal.data ? (
          <div>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Data e mbledhjes">
                {detailModal.data.meeting_date ? moment(detailModal.data.meeting_date).format('DD/MM/YYYY HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Njesia organizative">
                {detailModal.data.os?.name || detailModal.data.os_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Gjithsej">{detailModal.data.participants_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Meshkuj">{detailModal.data.males_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Femra">{detailModal.data.females_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Te rinj">{detailModal.data.youngs_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Prof. te lira">{detailModal.data.free_professions_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Anetare te rinj">{detailModal.data.new_members_count || 0}</Descriptions.Item>
              {detailModal.data.new_members_names && (
                <Descriptions.Item label="Emrat e anetareve" span={2}>{detailModal.data.new_members_names}</Descriptions.Item>
              )}
              {detailModal.data.headers_names && (
                <Descriptions.Item label="Kryetaret" span={2}>{detailModal.data.headers_names}</Descriptions.Item>
              )}
              {detailModal.data.proposed_delegate_name && (
                <Descriptions.Item label="Delegati" span={2}>{detailModal.data.proposed_delegate_name}</Descriptions.Item>
              )}
              {detailModal.data.comment && (
                <Descriptions.Item label="Koment" span={2}>{detailModal.data.comment}</Descriptions.Item>
              )}
            </Descriptions>

            {detailModal.data.images_list && detailModal.data.images_list.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }}>
                  <span style={{ color: BRAND, fontSize: 13 }}>Foto</span>
                </Divider>
                <Space wrap>
                  {detailModal.data.images_list.map((img, i) => (
                    <img
                      key={i}
                      src={getMediaUrl(img.image || img.photo || img.url)}
                      alt={`Foto ${i + 1}`}
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
                    />
                  ))}
                </Space>
              </div>
            )}

            {detailModal.data.video && (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }}>
                  <span style={{ color: BRAND, fontSize: 13 }}>Video</span>
                </Divider>
                <video
                  src={getMediaUrl(detailModal.data.video)}
                  controls
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                />
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
